/* Timestamps, rendered the way the screens already write them.
 *
 * The API sends ISO-8601 in UTC. The cockpit shows three different shapes of the
 * same instant, and each one is a deliberate choice the fixtures already made:
 *
 *   `20 Jul 2026, 09:14`  a case header — absolute, because "created" is a fact
 *                         someone may need to quote in a ticket.
 *   `2m ago`              last activity — relative, because the only question
 *                         being asked is "is this moving?".
 *   `Today · 09:42`       a timeline row — relative day, absolute clock, because
 *                         a sequence of events is read for its order and its
 *                         gaps at once.
 *
 * All three render in the reader's local zone, which is what `Date`'s accessors
 * do. An operator in Frankfurt reading `09:14` means their 09:14; showing them
 * UTC and letting them subtract is how a delivery date gets missed by an hour.
 *
 * Nothing here throws. An unparseable timestamp renders as an em dash, because a
 * screen that fails to draw because one row had a bad date is worse than a screen
 * with one dash in it.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** The dash the cockpit uses for "no value", not a hyphen. */
export const DASH = '—';

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

function clock(at: Date): string {
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

function day(at: Date): string {
  return `${at.getDate()} ${MONTHS[at.getMonth()]}`;
}

/** `20 Jul 2026, 09:14` — the absolute form, for headers. */
export function formatStamp(iso: string | null | undefined): string {
  const at = parse(iso);
  if (at === null) return DASH;
  return `${day(at)} ${at.getFullYear()}, ${clock(at)}`;
}

/** `12 Jun 2026` — the same instant with the clock dropped.
 *
 * For things whose unit is a day: when a template was last published, when a
 * policy took effect. Showing `12 Jun 2026, 00:00` there would imply the minute
 * was meaningful, and somebody would eventually read it as one. */
export function formatDate(iso: string | null | undefined): string {
  const at = parse(iso);
  if (at === null) return DASH;
  return `${day(at)} ${at.getFullYear()}`;
}

/** `2m ago` / `3h ago` / `1d ago` — the relative form, for "last activity".
 *
 * Coarsens as it goes back: minutes for the first hour, hours for the first day,
 * then days. Nobody reading a queue needs to know something happened 4,271
 * minutes ago, and a number that precise reads as more meaningful than it is.
 * Anything in the future renders as `just now` rather than as a negative — a
 * clock skew of a few seconds between the API and the browser is normal, and
 * `-1m ago` would make it look like a bug in the data. */
export function formatRelative(iso: string | null | undefined, now: Date): string {
  const at = parse(iso);
  if (at === null) return DASH;

  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/** `Today · 09:42`, `Yesterday · 15:53`, `20 Jul · 09:14` — the timeline form.
 *
 * Compared by calendar date rather than by elapsed hours: an event at 23:50 last
 * night is "Yesterday" at 00:30 even though it was forty minutes ago, because
 * that is what the reader means by yesterday. */
export function formatWhen(iso: string | null | undefined, now: Date): string {
  const at = parse(iso);
  if (at === null) return DASH;

  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const elapsed = midnight.getTime() - new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  const days = Math.round(elapsed / 86_400_000);

  if (days === 0) return `Today · ${clock(at)}`;
  if (days === 1) return `Yesterday · ${clock(at)}`;
  return `${day(at)} · ${clock(at)}`;
}
